/**
 * usePaymentVerification — when returning from Stripe with ?session_id=...,
 * polls /api/checkout/status/{sessionId} until paid (max 8 attempts).
 */
import { useEffect, useRef } from "react";
import { api } from "../lib/api";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export function usePaymentVerification(sessionId) {
    const startedRef = useRef(false);

    useEffect(() => {
        if (!sessionId || startedRef.current) return;
        startedRef.current = true;

        let attempts = 0;
        let timeoutId = null;
        let cancelled = false;

        const poll = async () => {
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid" || attempts >= MAX_ATTEMPTS) return;
                attempts += 1;
                timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
            } catch {
                // Intentionally silent: the main order-polling hook re-fetches
                // /orders/{id} and surfaces any error state in the UI there.
                // Logging here would double-fire on every network blip.
            }
        };
        poll();

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [sessionId]);
}
